import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@services/logger/logger';

interface Props {
  readonly children: ReactNode;
  readonly fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  readonly error: Error | null;
}

/**
 * Top-level safety net. Per-feature boundaries (route `errorElement`) catch
 * data-loading failures and present feature-specific UI. This boundary catches
 * the rest — render errors, JS exceptions outside the query layer.
 *
 * In production we never display raw error.message to users — leaks stack
 * traces / internal paths. Devs see full info via the logger sink.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('uncaught render error', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { fallback, children } = this.props;
    if (error) {
      if (fallback) return fallback(error, this.reset);
      return (
        <div role="alert" style={{ padding: 32, textAlign: 'center' }}>
          <h1 style={{ marginTop: 0 }}>Something went wrong</h1>
          <p style={{ color: 'var(--pfl-text-mute)' }}>
            The dashboard hit an unexpected error. The team has been notified.
          </p>
          <button type="button" onClick={this.reset}>
            Try again
          </button>
        </div>
      );
    }
    return children;
  }
}
